import { Alert } from '@tempo-io/sui-alert';
import { Badge } from '@tempo-io/sui-badge';
import { cn } from '@tempo-io/sui-class-utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSchedulingValidationMessage } from '@/components/loop-planner/common/getSchedulingValidationMessage';
import { useGetTeamQuery } from '@/lib/queries/teams/useGetTeamQuery';
import { useGetEstimationsByIds } from '@/lib/queries/work-integrations/useGetEstimations';
import { ITEM_TOOLTIP_DELAY, STALE_TIME_MS } from './TimelineItemConstants';
import type { TimelineItemType } from './useTimelineTableColumns';

const EMPTY_BAR_DAYS = 1;

export const EmptyScheduleBar = memo(
  ({
    item,
    leftOffset,
    itemHeight,
    itemTopOffset,
    onItemClick,
    dayWidth,
  }: {
    item: TimelineItemType;
    leftOffset: number;
    itemHeight: number;
    itemTopOffset: number;
    onItemClick: (itemId: number) => void;
    dayWidth: number;
  }) => {
    const { t } = useTranslation();
    const [isDelayedHover, setIsDelayedHover] = useState(false);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsDelayedHover(true);
      }, ITEM_TOOLTIP_DELAY);
    };

    const handleMouseLeave = () => {
      setIsDelayedHover(false);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };

    const { data: teamData, isLoading: isTeamLoading } = useGetTeamQuery({
      teamId: item.teamId ?? undefined,
      options: {
        staleTime: STALE_TIME_MS,
        enabled: !!item.teamId && isDelayedHover,
      },
    });

    const { data: estimationsByIds, isLoading: isEstimationSchemeLoading } = useGetEstimationsByIds(
      {
        estimationIds: new Set(item?.estimationId ? [item.estimationId] : []),
        enabled: !!item.estimationId && isDelayedHover,
        staleTime: STALE_TIME_MS,
      },
    );

    const barWidth = dayWidth * EMPTY_BAR_DAYS;

    const handleClick = () => {
      if (item.isDeletedInItemStore) return;
      onItemClick(item.id);
    };

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    return (
      <div
        className="absolute z-20"
        style={{
          top: itemTopOffset,
          left: leftOffset,
          width: barWidth,
          height: itemHeight,
        }}
      >
        <Tooltip open={isDelayedHover}>
          <TooltipTrigger asChild>
            {/* biome-ignore lint/a11y/useSemanticElements: TooltipTrigger with asChild requires non-button to avoid nesting */}
            <div
              className={cn('w-full h-full rounded-full bg-bg-warning', {
                'cursor-pointer': !item.isDeletedInItemStore,
                'cursor-default': item.isDeletedInItemStore,
              })}
              style={{
                minHeight: '20px',
              }}
              onClick={handleClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={item.name}
            />
          </TooltipTrigger>
          <TooltipContent
            align="start"
            side="bottom"
            className="bg-bg-surface-base text-text-neutral-primary border-2 min-w-64 w-max max-w-100"
          >
            <div className="flex flex-col gap-2 p-2">
              <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-2 items-center">
                <strong>{t(($) => $.common.filters.labels.items.effortEstimate)}:</strong>
                {!isEstimationSchemeLoading && estimationsByIds && item.estimationId ? (
                  <Badge variant="secondary" className="justify-self-end">
                    {estimationsByIds[item.estimationId]?.label}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="justify-self-end">
                    {t(($) => $.common.notEstimated)}
                  </Badge>
                )}
                <strong>{t(($) => $.loopVisualization.layout.headers.fields.team)}:</strong>
                {!isTeamLoading && teamData ? (
                  <Badge variant="secondary" className="justify-self-end">
                    {teamData.team.name}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="justify-self-end">
                    {t(($) => $.common.notAssigned)}
                  </Badge>
                )}
              </div>
              {item.schedulingValidations && item.schedulingValidations.length > 0 && (
                <>
                  <div className="border-b-2 text-text-neutral-secondary" />
                  <div className="max-w-64 flex flex-col gap-2">
                    {item.schedulingValidations.map((validation) => (
                      <Alert key={validation} variant="warning">
                        {getSchedulingValidationMessage(validation, t)}
                      </Alert>
                    ))}
                  </div>
                </>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  },
);

EmptyScheduleBar.displayName = 'EmptyScheduleBar';
